/**
 * Listing details editor (moved from ListingDetailPage.tsx's ListingFieldsPanel, minus screening
 * criteria, which is now ScreeningCriteriaEditor): rent, availability date, lease terms, fees and
 * move-in totals, pets, income limits, utilities, furnished, renter's insurance, screening
 * notes and the on-hold auto-reply. Jurisdiction warnings stay soft (BR-1a).
 *
 * The terms form state, validation and request body come from useListingTermsForm, and the fields from
 * ListingTermsFields: the same pair the guide's Listing details step uses.
 */

import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import {
  updateListing,
  LEASE_TERM_LABELS,
  UTILITY_LABELS,
  FURNISHED_LABELS,
  type Listing,
  type ConstraintWarning,
} from '../../../lib/leasing.api';
import { ListingTermsFields } from './ListingTermsFields';
import { formatDate, formatRentCents, useListingConstraints, WarningBanner } from './shared';
import { estimateMoveInTotalCents, termsValuesFromListing, useListingTermsForm } from './useListingTermsForm';

// ---------------------------------------------------------------------------
// Section: Listing Fields Editor
// ---------------------------------------------------------------------------

interface ListingFieldsEditorProps {
  listing: Listing;
  onUpdated: () => void;
  /** 'card' (Screening & details section): read-only view with Edit. 'bare' (setup row / sheet): the form only. */
  variant?: 'card' | 'bare';
  /** Bare variant: called after a successful save or on Cancel. */
  onDone?: () => void;
}

export function ListingFieldsEditor({ listing, onUpdated, variant = 'card', onDone }: ListingFieldsEditorProps) {
  const isBare = variant === 'bare';
  const [editing, setEditing] = useState(isBare);
  const form = useListingTermsForm(termsValuesFromListing(listing), {
    hadIncomeRestrictions: listing.incomeRestrictions != null,
  });
  const [screeningNotes, setScreeningNotes] = useState(listing.screeningNotes ?? '');
  const [pausedAutoReply, setPausedAutoReply] = useState(listing.pausedAutoReplyTemplate ?? '');

  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveWarnings, setSaveWarnings] = useState<ConstraintWarning[]>([]);

  const isClosed = listing.status === 'closed';

  const constraints = useListingConstraints(listing);

  const handleCancel = () => {
    setError(null);
    form.reset();
    if (isBare) {
      onDone?.();
      return;
    }
    setEditing(false);
  };

  const handleSaveListing = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaveWarnings([]);
    if (!form.validate()) return;
    setIsSaving(true);
    try {
      const result = await updateListing(listing.id, {
        ...form.toUpdateBody(),
        screeningNotes: screeningNotes || null,
        pausedAutoReplyTemplate: pausedAutoReply || null,
      });
      form.markSaved();
      onUpdated();
      if (result.warnings.length > 0) {
        // Soft (BR-1a): the save went through; keep the warnings in view — in a row/sheet the
        // form stays open so they are seen, and the landlord closes it with Cancel.
        setSaveWarnings(result.warnings);
        if (!isBare) setEditing(false);
        return;
      }
      if (isBare) {
        onDone?.();
        return;
      }
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save listing');
    } finally {
      setIsSaving(false);
    }
  };

  // Tenant move-in total of the saved listing: the same sum the form's estimate uses.
  const displayMoveInTotal = estimateMoveInTotalCents(termsValuesFromListing(listing));

  const body = (
    <>
        {saveWarnings.length > 0 && <WarningBanner warnings={saveWarnings} />}

        {editing ? (
          <form onSubmit={(e) => void handleSaveListing(e)} className="space-y-4" data-testid="listing-edit-form">
            <ListingTermsFields form={form} constraints={constraints} idPrefix="edit" testIdPrefix="listing-edit-" />

            <div>
              <label htmlFor="edit-screening-notes" className="block text-sm font-medium mb-1">Screening Notes</label>
              <textarea id="edit-screening-notes" data-testid="listing-edit-screening-notes" value={screeningNotes}
                onChange={(e) => setScreeningNotes(e.target.value)} rows={3}
                placeholder="Free-text notes for screening prospects…"
                className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring resize-y" />
            </div>
            {(listing.status === 'paused' || listing.status === 'draft') && (
              <div>
                <label htmlFor="edit-auto-reply" className="block text-sm font-medium mb-1">
                  On-Hold Auto-Reply Template <span className="text-muted-foreground font-normal ml-1">(when paused)</span>
                </label>
                <textarea id="edit-auto-reply" data-testid="listing-edit-auto-reply" value={pausedAutoReply}
                  onChange={(e) => setPausedAutoReply(e.target.value)} rows={3}
                  placeholder="Thanks for your interest — we're currently processing applications and will follow up if a slot opens."
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring resize-y" />
              </div>
            )}

            {error && (
              <div data-testid="listing-edit-error" className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm" role="alert">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button type="submit" disabled={isSaving} data-testid="listing-edit-save">{isSaving ? 'Saving…' : 'Save Changes'}</Button>
              <Button type="button" variant="outline" onClick={handleCancel} data-testid="listing-edit-cancel" disabled={isSaving}>Cancel</Button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <dt className="text-muted-foreground mb-0.5">Asking Rent</dt>
              <dd className="font-medium">{formatRentCents(listing.askingRentCents)}/mo</dd>
            </div>
            <div>
              <dt className="text-muted-foreground mb-0.5">Available</dt>
              <dd className="font-medium">{formatDate(listing.availableDate)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground mb-0.5">Accepted Lease Terms</dt>
              <dd className="font-medium" data-testid="listing-accepted-lease-terms">
                {listing.acceptedLeaseTerms.map((t) => LEASE_TERM_LABELS[t] ?? t).join(', ')}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground mb-0.5">Pets</dt>
              <dd className="font-medium">
                {listing.petPolicy?.petsAllowed ? 'Allowed' : 'Not allowed'}
                {listing.petPolicy?.petsAllowed && listing.petPolicy.petTypesAllowed && listing.petPolicy.petTypesAllowed.length > 0 && (
                  <span className="text-muted-foreground text-xs ml-1">
                    ({listing.petPolicy.petTypesAllowed.map((t) => t.replace(/_/g, ' ')).join(', ')})
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground mb-0.5">Smoking</dt>
              <dd className="font-medium">{listing.smokingAllowed ? 'Allowed' : 'Not allowed'}</dd>
            </div>
            {listing.fees?.applicationFeeCents != null && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Application Fee</dt>
                <dd className="font-medium">{formatRentCents(listing.fees.applicationFeeCents)}</dd>
              </div>
            )}
            {listing.fees?.securityDepositCents != null && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Security Deposit</dt>
                <dd className="font-medium">{formatRentCents(listing.fees.securityDepositCents)}</dd>
              </div>
            )}
            {listing.petPolicy?.petsAllowed && listing.fees?.petDepositCents != null && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Pet Deposit</dt>
                <dd className="font-medium">{formatRentCents(listing.fees.petDepositCents)}</dd>
              </div>
            )}
            {listing.petPolicy?.petsAllowed && listing.fees?.petRentMonthlyCents != null && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Pet Rent</dt>
                <dd className="font-medium">{formatRentCents(listing.fees.petRentMonthlyCents)}/mo</dd>
              </div>
            )}
            {listing.fees?.administrationFeeCents != null && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Administration Fee</dt>
                <dd className="font-medium">{formatRentCents(listing.fees.administrationFeeCents)}</dd>
              </div>
            )}
            {listing.fees?.holdingDepositCents != null && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Holding Deposit</dt>
                <dd className="font-medium">{formatRentCents(listing.fees.holdingDepositCents)}</dd>
              </div>
            )}
            {listing.fees?.parkingFeeMonthlyCents != null && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Parking Fee</dt>
                <dd className="font-medium">{formatRentCents(listing.fees.parkingFeeMonthlyCents)}/mo</dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground mb-0.5">First Month</dt>
              <dd className="font-medium">{(listing.fees?.firstMonthRequired ?? false) ? 'Required' : 'Not required'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground mb-0.5">Last Month</dt>
              <dd className="font-medium">{(listing.fees?.lastMonthRequired ?? false) ? 'Required' : 'Not required'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground mb-0.5">Furnished</dt>
              <dd className="font-medium">{FURNISHED_LABELS[listing.furnished] ?? listing.furnished}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground mb-0.5">Renters Insurance</dt>
              <dd className="font-medium">{listing.requireRentersInsurance ? 'Required' : 'Not required'}</dd>
            </div>
            {listing.utilitiesIncluded && listing.utilitiesIncluded.length > 0 && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-muted-foreground mb-0.5">Utilities Included</dt>
                <dd className="font-medium">{listing.utilitiesIncluded.map((u) => UTILITY_LABELS[u] ?? u).join(', ')}</dd>
              </div>
            )}
            <div className="col-span-2 sm:col-span-3">
              {/* Tenant Move-in Total — full prospect budget including application fee */}
              <dt className="text-muted-foreground mb-0.5">Tenant Move-in Total</dt>
              <dd className="font-medium mb-1">
                <ul className="space-y-0.5 text-sm font-normal text-muted-foreground mb-0.5">
                  {(listing.fees?.firstMonthRequired ?? false) && (
                    <li>First month's rent: {formatRentCents(listing.askingRentCents)}</li>
                  )}
                  {listing.fees?.securityDepositCents != null && (
                    <li>Security deposit: {formatRentCents(listing.fees.securityDepositCents)}</li>
                  )}
                  {(listing.fees?.lastMonthRequired ?? false) && (
                    <li>Last month's rent: {formatRentCents(listing.askingRentCents)}</li>
                  )}
                  {listing.fees?.applicationFeeCents != null && (
                    <li>Application fee: {formatRentCents(listing.fees.applicationFeeCents)}</li>
                  )}
                  {listing.petPolicy?.petsAllowed && listing.fees?.petDepositCents != null && (
                    <li>Pet deposit: {formatRentCents(listing.fees.petDepositCents)}</li>
                  )}
                  {listing.fees?.administrationFeeCents != null && (
                    <li>Administration fee: {formatRentCents(listing.fees.administrationFeeCents)}</li>
                  )}
                  {listing.fees?.holdingDepositCents != null && (
                    <li>Holding deposit: {formatRentCents(listing.fees.holdingDepositCents)}</li>
                  )}
                </ul>
                <span className="font-semibold" data-testid="listing-display-tenant-move-in-total">{formatRentCents(displayMoveInTotal)}</span>
              </dd>

              {/* Legal Move-in Total — jurisdiction cap calculation, excludes application fee and first month */}
              {constraints?.moveInCost.maxFirstMonthMultiplier != null && (() => {
                const _fees = listing.fees ?? {};
                const _petPolicy = listing.petPolicy ?? null;
                const legalTotal =
                  (_fees.securityDepositCents ?? 0) +
                  ((_fees.lastMonthRequired ?? false) ? listing.askingRentCents : 0) +
                  (constraints.moveInCost.petDepositCountedInCeiling && _petPolicy?.petsAllowed && _fees.petDepositCents != null
                    ? _fees.petDepositCents
                    : 0);
                const cap = constraints.moveInCost.maxFirstMonthMultiplier * listing.askingRentCents;
                const isOverCap = legalTotal > cap;
                return (
                  <div className="mt-3">
                    <dt className="text-muted-foreground mb-0.5">Legal Move-in Total <span className="text-xs">(jurisdiction cap)</span></dt>
                    <dd className="font-medium mb-1">
                      <ul className="space-y-0.5 text-sm font-normal text-muted-foreground mb-0.5">
                        {_fees.securityDepositCents != null && _fees.securityDepositCents > 0 && (
                          <li>Security deposit: {formatRentCents(_fees.securityDepositCents)}</li>
                        )}
                        {(_fees.lastMonthRequired ?? false) && (
                          <li>Last month's rent: {formatRentCents(listing.askingRentCents)}</li>
                        )}
                        {constraints.moveInCost.petDepositCountedInCeiling && _petPolicy?.petsAllowed && _fees.petDepositCents != null && (
                          <li>Pet deposit: {formatRentCents(_fees.petDepositCents)}</li>
                        )}
                      </ul>
                      <span className="font-semibold" data-testid="listing-display-legal-move-in-total">{formatRentCents(legalTotal)}</span>
                      <span className="text-xs text-muted-foreground ml-2">/ cap: {formatRentCents(cap)}</span>
                      {isOverCap && (
                        <span
                          data-testid="listing-display-legal-move-in-over-cap"
                          className="block text-xs text-warning mt-0.5"
                        >
                          Legal move-in total exceeds the jurisdiction cap of {formatRentCents(cap)}.
                        </span>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Application fee and first month&apos;s rent excluded from cap
                        {!constraints.moveInCost.petDepositCountedInCeiling && '. Pet deposit excluded'}.
                      </p>
                    </dd>
                  </div>
                );
              })()}
            </div>
            {constraints?.securityDeposit.installmentRequiredAboveMultiplier != null && (
              <div className="col-span-2 sm:col-span-3">
                <p data-testid="listing-display-installment-disclaimer" className="text-xs text-warning mt-1">
                  Tenants in {constraints.jurisdiction.displayName} may have the right to pay deposits and fees in installments (installment threshold: {constraints.securityDeposit.installmentRequiredAboveMultiplier}× rent).
                </p>
              </div>
            )}
            {listing.incomeRestrictions != null && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-muted-foreground mb-0.5">Income Restrictions (minimum monthly income per household size)</dt>
                <dd className="font-normal text-sm">
                  <ul className="space-y-0.5 text-muted-foreground">
                    {([
                      { key: 'onePersonCents', label: '1-person' },
                      { key: 'twoPersonCents', label: '2-person' },
                      { key: 'threePersonCents', label: '3-person' },
                      { key: 'fourPersonCents', label: '4-person' },
                      { key: 'fivePersonCents', label: '5-person' },
                      { key: 'sixPersonCents', label: '6-person' },
                      { key: 'sevenPersonCents', label: '7-person' },
                      { key: 'eightPersonCents', label: '8-person' },
                    ] as { key: keyof NonNullable<typeof listing.incomeRestrictions>; label: string }[])
                      .filter((hh) => listing.incomeRestrictions![hh.key] != null)
                      .map((hh) => (
                        <li key={hh.key}>{hh.label}: {formatRentCents(listing.incomeRestrictions![hh.key]!)}</li>
                      ))}
                  </ul>
                </dd>
              </div>
            )}
            {listing.screeningNotes && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-muted-foreground mb-0.5">Screening Notes</dt>
                <dd className="whitespace-pre-wrap">{listing.screeningNotes}</dd>
              </div>
            )}
            {listing.pausedAutoReplyTemplate && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-muted-foreground mb-0.5">On-Hold Auto-Reply</dt>
                <dd className="whitespace-pre-wrap text-muted-foreground italic">{listing.pausedAutoReplyTemplate}</dd>
              </div>
            )}
          </dl>
        )}
    </>
  );

  if (isBare) {
    return <div data-testid="listing-fields-panel">{body}</div>;
  }

  return (
    <section data-testid="listing-fields-panel" className="mb-6" aria-labelledby="listing-fields-title">
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 id="listing-fields-title" className="text-base font-semibold">
            Listing details
          </h2>
          {!isClosed && !editing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-[44px]"
              data-testid="listing-edit-button"
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          )}
        </div>
        {body}
      </div>
    </section>
  );
}
